import React, { useState, useMemo } from 'react';
import { ListFilter, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { ColumnFilterContent } from './ColumnFilterContent';
import { ALTERNATE_FILTER_OPERATOR } from './alternateFilter';
import { NumberRangeFilterSummary } from './NumberRangeFilterSummary';
import { Badge } from '@/components/ui/badge';
import { DateTime } from '@/components/ui/datetime';
import { cn } from '@/lib/utils';
import { useFilterOptions } from '../../hooks/useFilterOptions';
import type {
  FilterValue,
  FilterOptions,
  FilterOperatorObject,
  FilterOption,
  FilterTooltipOptions,
  ColumnDataType,
  AlternateColumnFilter,
} from '../../types';
import {
  ColumnDataTypes,
  FilterOperators,
  FilterOptionTypes,
  isValuelessOperator,
} from '../../types';

const toCssSize = (value?: number | string): string | undefined => {
  if (value === undefined) {
    return undefined;
  }

  return typeof value === 'number' ? `${value}px` : value;
};

/**
 * Renders tooltip content for a filter option using the fallback logic:
 * 1. enableCellRenderer - uses the defaultRender function (column's render) if defined
 * 2. render - uses the tooltip's custom render function
 * 3. labelFormatter - uses the tooltip's label formatter function
 * 4. Falls back to option label or value
 */
function renderTooltipContent(
  option: FilterOption,
  tooltipOptions?: FilterTooltipOptions,
  defaultRender?: (option: FilterOption, isSelected: boolean) => React.ReactNode
): React.ReactNode {
  // Fallback 1: enableCellRenderer - use defaultRender (column's render function)
  if (tooltipOptions?.enableCellRenderer && defaultRender) {
    return defaultRender(option, true);
  }

  // Fallback 2: enableCellRenderer with option's own render function
  if (tooltipOptions?.enableCellRenderer && option.render) {
    return option.render(option, true);
  }

  // Fallback 3: tooltip render function
  if (tooltipOptions?.render) {
    return tooltipOptions.render(option);
  }

  // Fallback 4: tooltip labelFormatter
  if (tooltipOptions?.labelFormatter) {
    return tooltipOptions.labelFormatter(option.value);
  }

  // Default fallback: option label or value
  return option.label ?? option.value;
}

export interface ColumnFilterProps {
  /** The field id/key for the filter */
  fieldId: string;
  /** Display name for the filter (e.g., 'Source', 'Publisher') */
  fieldName: string;
  /** Optional description shown in the filter popover bulk actions tooltip. */
  description?: string;
  /** Available options to filter - can be static or dynamic */
  filterOptions: FilterOptions;
  /** Available operators for this column */
  availableOperators: FilterOperatorObject[];
  /** Column data type for determining the value input component */
  columnDataType: ColumnDataType;
  /** Numeric range metadata for number filters */
  range?: {
    min: number;
    max: number;
    step?: number;
  };
  /** Callback when filter is applied */
  onFilterChange: (filter: FilterValue) => void;
  /** Callback when filter is cancelled */
  onCancel?: () => void;
  /** Optional class name */
  className?: string;
  /** Current active filter */
  currentFilter?: FilterValue;
  /** Whether to include an empty/None option at the top */
  includeEmptyOption?: boolean;
  /** Label for the empty option (defaults to "None") */
  emptyOptionLabel?: string;
  /** KIR-5098: a second field this filter can drive (see AlternateColumnFilter). */
  alternateFilter?: AlternateColumnFilter;
}

/**
 * ColumnFilter is a wrapper component that renders the ColumnFilterContent
 * inside a popover with a filter icon trigger.
 */
export const ColumnFilter: React.FC<ColumnFilterProps> = ({
  fieldId,
  fieldName,
  description,
  filterOptions,
  availableOperators,
  columnDataType,
  range,
  onFilterChange,
  onCancel,
  className,
  currentFilter,
  includeEmptyOption = false,
  emptyOptionLabel = 'None',
  alternateFilter,
}) => {
  // KIR-5098: the alternate field is picked through its own dropdown entry.
  const isAlternateActive =
    !!alternateFilter && currentFilter?.field === alternateFilter.field;
  const operatorsWithAlternate = useMemo(
    () =>
      alternateFilter
        ? [
            ...availableOperators,
            { value: ALTERNATE_FILTER_OPERATOR, label: alternateFilter.label },
          ]
        : availableOperators,
    [availableOperators, alternateFilter]
  );
  const [open, setOpen] = useState(false);
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const paginationLimit =
    filterOptions.type === FilterOptionTypes.DYNAMIC
      ? filterOptions.pagination?.limit
      : undefined;

  // Computed before useFilterOptions so it can control the enabled flag.
  const isFilterActive =
    currentFilter &&
    (currentFilter.field === fieldId || isAlternateActive) &&
    (currentFilter.value.length > 0 ||
      isValuelessOperator(currentFilter.operator));

  const {
    options,
    total,
    isLoading,
    isError,
    refetch,
    defaultRender,
    hasMore,
    isFetchingMore,
    onLoadMore,
    tooltipOptions,
  } = useFilterOptions(filterOptions, {
    search: debouncedSearch || undefined,
    limit: paginationLimit,
    // Also enable when filter is active so tooltip labels resolve without
    // requiring the dropdown to have been opened first (e.g. URL-loaded filters).
    enabled: open || Boolean(isFilterActive),
  });

  // Add empty option at the top if requested
  const optionsWithEmpty = useMemo(
    () =>
      includeEmptyOption
        ? [{ value: '', label: emptyOptionLabel }, ...options]
        : options,
    [includeEmptyOption, emptyOptionLabel, options]
  );

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) setDebouncedSearch('');
    setOpen(nextOpen);
  };

  const handleApply = (filter: FilterValue) => {
    // KIR-5098: the primary and alternate fields are exclusive — clear the
    // other one so a single column never carries two filters.
    if (alternateFilter) {
      const other =
        filter.field === alternateFilter.field
          ? { field: fieldId, operator: availableOperators[0].value }
          : {
              field: alternateFilter.field,
              operator: alternateFilter.operator,
            };
      onFilterChange({ ...other, value: [] });
    }
    onFilterChange(filter);
    handleOpenChange(false);
  };

  const handleClear = () => {
    // Use a value-requiring operator so an empty value reads as a removal;
    // value-less operators stay "complete" when empty and would not be removed.
    const removalOperator =
      availableOperators.find(op => !isValuelessOperator(op.value))?.value ??
      availableOperators[0].value;
    onFilterChange({
      field: fieldId,
      operator: removalOperator,
      value: [],
    });
    if (alternateFilter) {
      onFilterChange({
        field: alternateFilter.field,
        operator: alternateFilter.operator,
        value: [],
      });
    }
    handleOpenChange(false);
  };

  const handleInlineClear = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    event.preventDefault();
    handleClear();
  };

  const handleInlineClearKeyDown = (
    event: React.KeyboardEvent<HTMLElement>
  ) => {
    if (event.key !== 'Enter' && event.key !== ' ') {
      return;
    }

    event.preventDefault();
    handleClear();
  };

  const handleCancel = () => {
    onCancel?.();
    handleOpenChange(false);
  };

  const activeValues = useMemo(
    () => (isFilterActive ? currentFilter.value : []),
    [isFilterActive, currentFilter]
  );
  const activeValueCount = activeValues.filter(value => value !== '').length;
  const isValuelessFilterActive = Boolean(
    isFilterActive &&
    currentFilter &&
    isValuelessOperator(currentFilter.operator)
  );

  // Get active options with their tooltip content.
  // A label collision stores every raw id on one option (metadata.mergedValues).
  // Show that option once, so copilot and github_copilot_cli do not list as
  // two rows after the shared "GitHub Copilot" choice is checked.
  const activeOptionsWithTooltip = useMemo(() => {
    const seenOptionValues = new Set<string>();
    const items: { value: string; tooltipContent: React.ReactNode }[] = [];

    for (const value of activeValues) {
      if (value === '') {
        items.push({ value, tooltipContent: emptyOptionLabel });
        continue;
      }

      const matchedOption = optionsWithEmpty.find(option => {
        const merged = option.metadata?.mergedValues;
        if (Array.isArray(merged) && merged.includes(value)) {
          return true;
        }
        return option.value === value;
      });

      if (matchedOption) {
        if (seenOptionValues.has(matchedOption.value)) {
          continue;
        }
        seenOptionValues.add(matchedOption.value);
        items.push({
          value: matchedOption.value,
          tooltipContent: renderTooltipContent(
            matchedOption,
            tooltipOptions,
            defaultRender
          ),
        });
        continue;
      }

      items.push({
        value,
        tooltipContent: tooltipOptions?.labelFormatter
          ? tooltipOptions.labelFormatter(value)
          : value,
      });
    }

    return items;
  }, [
    activeValues,
    emptyOptionLabel,
    optionsWithEmpty,
    tooltipOptions,
    defaultRender,
  ]);

  // Operator badge label — the same configured label the dropdown's operator
  // selector shows. Derived purely from the active filter + column config; no
  // per-operator map. Absent match → no badge (values still render).
  const operatorLabel = useMemo(
    () =>
      isAlternateActive
        ? alternateFilter?.label
        : availableOperators.find(
            operator => operator.value === currentFilter?.operator
          )?.label,
    [availableOperators, currentFilter, isAlternateActive, alternateFilter]
  );

  // Date columns format each boundary through the shared DateTime, to the
  // minute (boundaries are picked to the minute); the path is keyed on the
  // column's data type, never on its id/field name.
  const isDateColumn = columnDataType === ColumnDataTypes.DATE;
  const isNumberColumn =
    (isAlternateActive ? alternateFilter?.dataType : columnDataType) ===
    ColumnDataTypes.NUMBER;
  const isBetweenRange = currentFilter?.operator === FilterOperators.BETWEEN;

  // Never disabled while options load (KIR-4868): the options query also runs
  // whenever a filter is active (for tooltip labels), so a slow or hung request
  // would otherwise lock the user out of changing or clearing that filter. The
  // popover shows its own loading/error state for the value list, and
  // operators like Contains don't need the list at all.
  const filterButton = (
    <Button
      variant="ghost"
      size="icon-sm"
      className={cn('relative overflow-visible', className)}
      title={`Filter by ${fieldName}`}
    >
      {isFilterActive && (activeValueCount > 0 || isValuelessFilterActive) && (
        <span className="absolute -top-1 -right-1">
          <span
            role="button"
            tabIndex={0}
            className="flex h-3 w-3 items-center justify-center rounded-full border border-border bg-background text-foreground shadow-sm transition-colors hover:bg-muted"
            aria-label={`Clear ${fieldName} filter`}
            onClick={handleInlineClear}
            onKeyDown={handleInlineClearKeyDown}
          >
            <X className="h-2 w-2" />
          </span>
        </span>
      )}
      <ListFilter
        className={cn(
          'size-4',
          isFilterActive ? 'text-accent-foreground' : 'text-muted-foreground'
        )}
        strokeWidth={isFilterActive ? 2.75 : 2}
      />
    </Button>
  );

  const {
    width: dropdownWidth,
    minWidth: dropdownMinWidth,
    maxWidth: dropdownMaxWidth,
    className: dropdownClassName,
    ...popoverProps
  } = filterOptions.dropdown ?? {};

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      {isFilterActive ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <PopoverTrigger asChild>{filterButton}</PopoverTrigger>
          </TooltipTrigger>
          <TooltipContent align="start" className="max-w-64">
            {operatorLabel && (
              <Badge variant="secondary" className="text-xs">
                {operatorLabel}
              </Badge>
            )}
            {isDateColumn ? (
              isBetweenRange ? (
                <div className="mt-2 flex flex-col gap-1">
                  <div className="flex gap-2 text-xs">
                    <span className="w-10 shrink-0 text-primary-foreground/70">
                      From
                    </span>
                    <DateTime
                      value={activeValues[0]}
                      format="minute"
                      className="whitespace-normal"
                    />
                  </div>
                  <div className="flex gap-2 text-xs">
                    <span className="w-10 shrink-0 text-primary-foreground/70">
                      To
                    </span>
                    <DateTime
                      value={activeValues[1]}
                      format="minute"
                      className="whitespace-normal"
                    />
                  </div>
                </div>
              ) : (
                <ul className="mt-2 flex flex-col gap-1">
                  {activeValues.map((value, index) => (
                    <li key={`${value}-${index}`} className="text-xs">
                      <DateTime
                        value={value}
                        format="minute"
                        className="whitespace-normal"
                      />
                    </li>
                  ))}
                </ul>
              )
            ) : isNumberColumn ? (
              <NumberRangeFilterSummary values={activeValues} />
            ) : (
              <ul className="mt-2 flex flex-col gap-1">
                {activeOptionsWithTooltip.map((item, index) => (
                  <li key={`${item.value}-${index}`} className="text-xs">
                    {item.tooltipContent}
                  </li>
                ))}
              </ul>
            )}
          </TooltipContent>
        </Tooltip>
      ) : (
        <PopoverTrigger asChild>{filterButton}</PopoverTrigger>
      )}
      <PopoverContent
        className={cn(
          'p-0 overflow-x-hidden',
          !dropdownWidth &&
            !dropdownMinWidth &&
            !dropdownMaxWidth &&
            'min-w-72 max-w-96',
          dropdownClassName
        )}
        style={{
          width: toCssSize(dropdownWidth),
          minWidth: toCssSize(dropdownMinWidth),
          maxWidth: toCssSize(dropdownMaxWidth),
        }}
        align="start"
        {...popoverProps}
      >
        <ColumnFilterContent
          fieldId={fieldId}
          fieldName={fieldName}
          description={description}
          options={optionsWithEmpty}
          availableOperators={operatorsWithAlternate}
          columnDataType={columnDataType}
          range={range}
          onApply={handleApply}
          alternateFilter={alternateFilter}
          onCancel={handleCancel}
          currentFilter={currentFilter}
          emptyOptionLabel={emptyOptionLabel}
          defaultRender={defaultRender}
          onDebouncedSearchChange={setDebouncedSearch}
          suggestInput={
            filterOptions.type === FilterOptionTypes.DYNAMIC
              ? filterOptions.suggestInput
              : undefined
          }
          isLoading={isLoading}
          isError={isError}
          onRetry={refetch}
          hasMore={hasMore}
          isFetchingMore={isFetchingMore}
          onLoadMore={onLoadMore}
          totalOptionsCount={total}
        />
      </PopoverContent>
    </Popover>
  );
};
